import React from 'react';
import { config } from '../../../config';
import s from './solution-breadcrumbs.scss';
import type { IRoute } from '../../..';
import { Path } from '../../..';
import { biLogger } from '../../../bi';
import {
  appMarketBreadcrumbsClick,
  appMarketSubCategoryClick,
} from '@wix/bi-logger-market/v2';
import { Breadcrumbs } from '@wix/design-system';
import { AppMarketLink } from '../../app-market-link';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import { withTranslation } from '../../../component-decorators/with-translation';
import type { WebSolution, WSTag } from '@wix/ambassador-marketplace/types';
import type { MarketplaceStore } from '../../../component-decorators/with-marketplace-store';
import { withMarketplaceStore } from '../../../component-decorators/with-marketplace-store';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { withExperiments } from '../../../component-decorators/with-experiments';

const enum BreadcrumbsItemId {
  HOME = 0,
  CATEGORY = 1,
  SUBCATEGORY = 2,
  APP = 3,
}

export const dtoToBreadcrumbsSectionProps = (
  solutionDto: WebSolution & {
    categories: {
      slug: string;
      name: string;
    }[];
  },
  prevRoute: IRoute,
): ISolutionBreadcrumbsSectionProps => {
  const props: ISolutionBreadcrumbsSectionProps = {
    categories: solutionDto.categories,
    tags: solutionDto.baseInfo.Tags,
    name: solutionDto.baseInfo.name,
    id: solutionDto.baseInfo.id,
  };
  if (prevRoute && prevRoute.path === Path.CATEGORY) {
    props.prevCategory = prevRoute.slug;
    props.prevSubcategory =
      prevRoute.subCategories && prevRoute.subCategories[0];
  }
  return props;
};

interface IBreadcrumbsItem {
  id: number;
  value: string;
}

export interface ISolutionBreadcrumbsSectionProps
  extends Partial<InjectedExperimentsProps>,
    Partial<WithTranslationProps> {
  name: string;
  id: string;
  tags: WSTag[];
  categories: {
    slug: string;
    name: string;
  }[];
  prevCategory?: string;
  prevSubcategory?: string;
  marketplaceStore?: MarketplaceStore;
}

export interface ISolutionBreadcrumbsSectionState {
  category: string;
  subcategory: string;
}

const filterCategoriesByTag = (
  tags: WSTag[],
  categories: {
    slug: string;
    name: string;
  }[],
  experiments: InjectedExperimentsProps['experiments'],
): string => {
  const newTagsBackendExperimentEnabled = experiments?.enabled?.(
    'com.wixpress.devcenter.market.specs.ShouldUseNewTagsService',
  );
  if (newTagsBackendExperimentEnabled) {
    return categories[0]?.slug;
  }

  if (tags && tags.length) {
    const categories = tags.filter(
      (tag) => tag.type === 'CATEGORY' && !tag.parentSlug && !tag.isHidden,
    );
    if (categories.length) {
      return categories[0].slug;
    }
  }
};

const filterSubcategoriesByCategory = (category: string, tags: WSTag[]) => {
  if (tags && tags.length) {
    const subcategories = tags.filter(
      (tag) =>
        tag.type === 'CATEGORY' && tag.parentSlug === category && !tag.isHidden,
    );
    if (subcategories.length) {
      return subcategories[0].slug;
    }
  }
};

const getTagNameBySlug = (
  slug: string,
  tags: WSTag[],
  categories: {
    slug: string;
    name: string;
  }[],
  experiments: InjectedExperimentsProps['experiments'],
) => {
  const newTagsBackendExperimentEnabled = experiments?.enabled?.(
    'com.wixpress.devcenter.market.specs.ShouldUseNewTagsService',
  );

  /*
    slug can be a category slug or subcategory slug
    and when com.wixpress.devcenter.market.specs.ShouldUseNewTagsService is on
    tags which is coming from websolution will not contain categories
    so in that case we will take the category name from categories which is fetched from new tags
  */
  const requestedTag = tags.find((tag) => tag.slug === slug);

  if (newTagsBackendExperimentEnabled) {
    const tagName = requestedTag && requestedTag.name;
    if (tagName) {
      return tagName;
    }

    const categoryName = categories.find(
      (category) => category.slug === slug,
    )?.name;

    return categoryName ?? '';
  }

  return (requestedTag && requestedTag.name) || slug;
};

@withTranslation
@withExperiments
@withMarketplaceStore
export class SolutionBreadcrumbsSection extends React.Component<
  ISolutionBreadcrumbsSectionProps,
  ISolutionBreadcrumbsSectionState
> {
  constructor(props) {
    super(props);
    if (this.props.tags && this.props.tags.length) {
      const category =
        this.props.prevCategory && this.props.prevCategory !== 'holiday-sale'
          ? this.props.prevCategory
          : filterCategoriesByTag(
              this.props.tags,
              this.props.categories,
              this.props.experiments,
            );
      const subcategory = this.props.prevSubcategory
        ? this.props.prevSubcategory
        : filterSubcategoriesByCategory(category, this.props.tags);
      this.state = { category, subcategory };
    }
  }

  getBreadcrumbs = (): IBreadcrumbsItem[] => {
    if (
      !this.state ||
      !this.state.category ||
      !this.state.subcategory ||
      // @ts-expect-error
      this.props.tags === []
    ) {
      return [];
    }

    const itemsValue: string[] = [
      this.props.t('breadcrumbs.home'),
      getTagNameBySlug(
        this.state.category,
        this.props.tags,
        this.props.categories,
        this.props.experiments,
      ),
      getTagNameBySlug(
        this.state.subcategory,
        this.props.tags,
        this.props.categories,
        this.props.experiments,
      ),
      this.props.name,
    ];
    return itemsValue.map((value: string, id: number) => {
      return {
        id,
        value,
        customElement: (
          <AppMarketLink
            className={s.breadcrumbLink}
            to={this.getHref({ id, value })}
          >
            {value}
          </AppMarketLink>
        ),
      };
    });
  };

  getItemAction = (item: IBreadcrumbsItem) => {
    const tagName = this.props.id;
    const tagType = 'solution_page';
    const msid = this.props?.marketplaceStore?.metaSiteId;

    switch (item.id) {
      case BreadcrumbsItemId.HOME: {
        biLogger.report(
          appMarketBreadcrumbsClick({
            nav_level: BreadcrumbsItemId.HOME,
            tag_name: tagName,
            tag_name_clicked: 'homepage',
            tag_type: tagType,
            tag_type_clicked: 'homepage',
          }),
        );
        config.goto({ path: Path.HOME });
        break;
      }
      case BreadcrumbsItemId.CATEGORY: {
        biLogger.report(
          appMarketBreadcrumbsClick({
            nav_level: BreadcrumbsItemId.CATEGORY,
            tag_name: tagName,
            tag_name_clicked: this.state.category.replace(/-/g, '_'),
            tag_type: tagType,
            tag_type_clicked: Path.CATEGORY,
          }),
        );
        config.goto({ path: Path.CATEGORY, slug: this.state.category });
        break;
      }
      case BreadcrumbsItemId.SUBCATEGORY: {
        biLogger.report(
          appMarketBreadcrumbsClick({
            nav_level: BreadcrumbsItemId.SUBCATEGORY,
            tag_name: tagName,
            tag_name_clicked: this.state.subcategory.replace(/-/g, '_'),
            tag_type: tagType,
            tag_type_clicked: Path.CATEGORY,
          }),
        );
        // add experiment
        biLogger.report(
          appMarketSubCategoryClick({
            type: 'breadcrumbs',
            subcategory_name: this.state.subcategory.replace(/-/g, '_'),
            category_name: this.state.category.replace(/-/g, '_'),
            msid,
          }),
        );
        config.goto({
          path: Path.CATEGORY,
          slug: this.state.category,
          subCategories: [this.state.subcategory],
          referral: 'breadcrumbs',
        });
      }
    }
  };

  componentDidUpdate(prevProps): void {
    if (this.props.id !== prevProps.id) {
      const category = filterCategoriesByTag(
        this.props.tags,
        this.props.categories,
        this.props.experiments,
      );
      const subcategory = filterSubcategoriesByCategory(
        category,
        this.props.tags,
      );
      this.setState({ category, subcategory });
    }
  }

  getHref = (item) => {
    const hrefMap = {
      [BreadcrumbsItemId.HOME]: '',
      [BreadcrumbsItemId.CATEGORY]: `${Path.CATEGORY}/${this.state.category}`,
      [BreadcrumbsItemId.SUBCATEGORY]: `${Path.CATEGORY}/${this.state.category}/${this.state.subcategory}`,
    };
    return item.value.toLowerCase() === 'home'
      ? ''
      : hrefMap[item.id] || item.value.toLowerCase();
  };

  render() {
    const items: IBreadcrumbsItem[] = this.getBreadcrumbs();

    return items ? (
      <div className={s.breadcrumbsContainer}>
        <Breadcrumbs
          dataHook="solution-breadcrumbs-section"
          items={items}
          theme="onWhiteBackground"
          onClick={(item: IBreadcrumbsItem) => this.getItemAction(item)}
          activeId={3}
        />
      </div>
    ) : (
      <div />
    );
  }
}
